/// <reference path="../../../atlas.d.ts" />
import { Component, OnInit, Input, ViewChild, Output, EventEmitter, ElementRef, ContentChild, ViewContainerRef, TemplateRef, EmbeddedViewRef } from '@angular/core';
import { MapLoaderService } from '../../services';
import { MapPointGroup } from '../../models';
import { AtlasPopupDirective } from '../../directives';

export interface CustomIcon {
  name: string;
  path: string;
}

@Component({
  selector: 'azure-atlas-map',
  template: `
    <div #atlasMap id="map"></div>
    <div id="popupWrapper">
      <div #popupsContainer></div>
    </div>
  `,
  styleUrls: ['./atlas-map.component.scss']
})
export class AtlasMapComponent implements OnInit {

  @Input() _config: atlas.ServiceOptions & atlas.CameraOptions & atlas.StyleOptions & atlas.UserInteractionOptions;
  @Input() _id: string;

  @ViewChild('atlasMap', { read: ElementRef }) apcmap: ElementRef;
  @ViewChild('popupsContainer', { read: ViewContainerRef }) popupsContainer: ViewContainerRef;
  @ContentChild(AtlasPopupDirective, { read: TemplateRef }) popupTemplate: TemplateRef<any>;
  public popupView: EmbeddedViewRef<any>;

  @Output() loaded: EventEmitter<any> = new EventEmitter();

  public map: atlas.Map;
  public popup: atlas.Popup;

  constructor(private mapService: MapLoaderService) { }

  ngOnInit(): void {
    this.mapService.loadedComponent.next(true);
  }

  ngAfterContentInit(): void {
    try {
      this.apcmap.nativeElement.setAttribute('id', this._id);
      this.map = new atlas.Map(this._id, this._config);
    } catch (e) {
      console.log(e);
    }
  }

  ngAfterViewInit() {
    this.emitLoaded();
  }

  public resizeMap(height: number, width: number) {
    this.map.resize(height, width);
  }

  /**
   * Emit Loaded event when the map is successfully loaded.
   */
  emitLoaded() {
    if (this.map && this.mapService.isLoaded) {
      this.loaded.emit();
    } else {
      setTimeout(this.emitLoaded, 1000);
    }
  }

  /**
   * Add Icons to the map
   * @param icons - Array of Icons specifying the icon name and path url.
   */
  addCustomIcons(icons: CustomIcon[]): void {
    if (this.map && this.mapService.isLoaded) {
      Promise.all(
        icons.map(x => {
          return this.map.imageSprite.add(x.name, x.path);
        })
      );
    } else {
      setTimeout(this.addCustomIcons, 400);
    }
  }

  /**
   * Create a datasource, add points, add layer to map
   * @param PointGroup - Group of Points filtered from user's data source
   */
  createPoints(PointGroup: MapPointGroup) {
    // Step 1: Add the datasource to the map
    this.map.sources.add(PointGroup.datasource);
    // Step 2: Add the points to the pre-existing datasource that is now mapped to the atlas
    PointGroup.points.forEach(feature => {
      PointGroup.datasource.add(feature.point);
    });
    // Step 3: Ass the Layer to the map
    this.map.layers.add(PointGroup.layer);
    // Step 4: Instantiate the popup
    this.popup = new atlas.Popup({ position: [0, 0], pixelOffset: [0, -18]});
    // Step 5: Add the popup functions to the events manager;
    this.map.events.add('click', PointGroup.layer, (e: any) => {
      this.openPopup(e);
    });
  }

  public createComponent(context: any): void {
    if (this.popupView) {
      this.popupView.destroy();
    }
    this.popupView = this.popupsContainer.createEmbeddedView(this.popupTemplate, context);
  }

  //#region Map Util Info Functions
  changeMapCamera(options: atlas.CameraOptions & atlas.AnimationOptions): void {
    this.map.setCamera(options);
  }

  changeMapStyle(options: atlas.StyleOptions): void {
    this.map.setStyle(options);
  }

  changeUserInteraction(options: atlas.UserInteractionOptions): void {
    this.map.setUserInteraction(options);
  }

  removeMap() {
    this.map.dispose();
  }

  getUniqueLayers(): string[] {
    return this.map.layers.getLayers().filter(this.onlyUnique);
  }

  private onlyUnique(value, index, self) {
    return self.indexOf(value) === index;
  }
  //#endregion

  //#region Popup Functions
  openPopup(e: any) {
    if(e.shapes && e.shapes.length > 0) {
      // By default the popup will show where the mouseevent occured
      let properties = e.shapes[0].getProperties();
      this.popup = new atlas.Popup();
      this.popup.setOptions({
        position: e.shapes[0].getCoordinates(),
        // TODO: Implement a ngTemplate for all popups
        content: '<div class="popup">' + properties.patientName + '</div>',
        closeButton: true,
        pixelOffset: [0, -18]
      });
      this.popup.open(e.map);
    }
  }

  closePopup(e: any) {
    this.popup.close();
  }
  //#endregion

}
